import { Collapse, Modal, Stack } from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
  CSSObject,
  SxProps,
  styled,
  Theme,
  useTheme,
} from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import deskdayWordmark from "assets/logo/deskdayWordmark.png";
import logoBig from "assets/logo/logoBig.png";
import {
  BillingInvoiceFilledIcon,
  BillingInvoiceIcon,
  BookFilledIcon,
  BookIcon,
  CalenderTimerFilledIcon,
  CalenderTimerIcon,
  CircularLoader,
  ControlcenterFilledIcon,
  ControlcenterIcon,
  CustomersFilledIcon,
  CustomersIcon,
  DevicemanagementFilledIcon,
  DevicemanagementIcon,
  DocumentationFilledIcon,
  DocumentationIcon,
  DownArrowIcon,
  DownloadIcon,
  HelenaAgentFilledIcon,
  HelenaAgentIcon,
  HomeFilledIcon,
  HomeIcon,
  NewDot,
  NewTag,
  ReportsFilledIcon,
  ReportsIcon,
  RMMFilledIcon,
  RMMIcon,
  SDFilledIcon,
  SDIcon,
  Tooltip,
} from "components";
import {
  useGlobalStore,
  useNonPersistantMainStore,
  usePermissionStore,
} from "controller";
import * as React from "react";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { DownloadDrawer, PERMISSIONS, Route } from "shared";
import PinnedChat from "shared/features/pinned-chat/PinnedChat";
import ddlogo from "../assets/deskdaylogo.png";
import { routerConstants } from "../constants/router-constants";
import { ReturnContext } from "../contexts/return-context";
import { TopBarComponent, WhatsNewButton } from "./component";
import { OnboardingProgressButton } from "./component/OnboardingProgressButton";

const drawerWidth = 272;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  background: "linear-gradient(185.95deg, #001E25 0%, #0D0F23 100%);",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: 200,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  background: "linear-gradient(185.95deg, #001E25 0%, #0D0F23 100%);",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: 200,
  }),
  overflowX: "hidden",
  width: "80px",
  [theme.breakpoints.up("sm")]: {
    width: "80px",
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  boxShadow: "none",
  backgroundColor: "#fff",
  px: 0,
  width: "calc(100% - 80px)",
  borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: 200,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    // width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: 200,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  background: "linear-gradient(185.95deg, #001E25 0%, #0D0F23 100%);",
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  justifyContent: "center",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export const MainLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // const userRole = useGlobalStore((state) => state.role) || "";
  const [enableReturn, setEnableReturn] = useState(true);
  const checkPermission = usePermissionStore((state) => state.hasPermissions);
  const isSuperAdmin = usePermissionStore((state) => state.isSuperAdmin);
  const isAdmin = usePermissionStore((state) => state.isAdmin);
  const [open, setOpen] = React.useState(false);
  const [openedTab, setOpenedTab] = useState("");
  const [lastOpenedTab, setLastOpenedTab] = useState("");
  const [isOnboardingPopupOpen, setIsOnboardingPopupOpen] = useState(true);
  const brandingLogoData = useGlobalStore((state) => state.brandingLogoData);
  const isHelenaActive = useGlobalStore((state) => state.isHelenaActive);
  const featureFlags = useNonPersistantMainStore((state) => state.featureFlags);

  const havePermissionForChat = usePermissionStore((state) =>
    state.hasPermissions(
      [
        PERMISSIONS.SD.SD_TICKET_ALL_RW,
        PERMISSIONS.SD.SD_TICKET_ASSIGNED_RW,
        PERMISSIONS.SD.SD_TASK_ALL_RW,
        PERMISSIONS.SD.SD_TASK_ASSIGNED_RW,
      ],
      "OR",
    ),
  );
  const toggleDrawer = () => {
    // setOpenedTab("");
    setOpen((prev) => {
      prev ? setOpenedTab("") : setOpenedTab(location.pathname);
      return !prev;
    });
  };

  const toggleCollapse = ({ route, menu }: { route: string; menu: Route }) => {
    if (!open) {
      // only open if menu item have children
      menu.hasChildren && setOpen(true);
      // if (
      //   menu.route === "/app/control-center" &&
      //   ["ADMIN"].includes(userRole)
      // ) {
      //   navigate("/app/control-center/configurations/home");
      // }
    }
    if (menu.children?.length === 0) {
      navigate(menu.route);
    }
    setOpenedTab((prev) => (prev.includes(route) ? "" : route));
    // openedTab.includes(menu.route)
  };

  return (
    <>
      <ReturnContext.Provider value={{ enableReturn, setEnableReturn }}>
        <Box sx={{ display: "flex", height: "100vh", position: "relative" }}>
          <CssBaseline />
          <AppBar position="fixed" open={open}>
            <Toolbar
              disableGutters
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: 0,
                margin: 0,
              }}
            >
              <Box width={"100%"}>
                <TopBarComponent enableReturn={true} />
              </Box>
            </Toolbar>
          </AppBar>
          <Drawer
            // onMouseEnter={() => {
            //   setOpenedTab(lastOpenedTab);
            //   setOpen(true);
            // }}
            onMouseLeave={() => {
              setLastOpenedTab(openedTab);
              setOpenedTab("");
              setOpen(false);
            }}
            variant="permanent"
            open={open}
            PaperProps={{
              sx: {
                // INFO: Fixes the issue of opening sidemenu on moving cursor to right side edges
                // common drawer zindex is 900
                zIndex: 898,
              },
            }}
            sx={{
              overflowX: "hidden",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              // zIndex: 1000,
            }}
          >
            <DrawerHeader
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "flex-start",
                pl: "24px",
                flexShrink: 0,
              }}
            >
              {open ? (
                <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
                  {brandingLogoData?.logoData && (
                    <img
                      src={brandingLogoData?.logoData || ddlogo}
                      width="32px"
                      height="32px"
                      style={{
                        objectFit: "cover",
                      }}
                      alt={"DeskDay"}
                    />
                  )}
                  <img
                    src={
                      brandingLogoData?.wordmarkData
                        ? brandingLogoData?.wordmarkData
                        : brandingLogoData?.logoData
                          ? deskdayWordmark
                          : logoBig
                    }
                    height="34px"
                    width="auto"
                    alt={"DeskDay"}
                    style={{
                      objectFit: "cover",
                    }}
                  />
                </Stack>
              ) : (
                <img
                  src={brandingLogoData?.logoData || ddlogo}
                  width="32px"
                  height="32px"
                  style={{
                    objectFit: "cover",
                  }}
                  alt={"DeskDay"}
                />
              )}
            </DrawerHeader>
            <Divider />
            <Box
              sx={{
                overflowY: "auto",
                overflowX: "hidden",
                flexGrow: 1,
                "&:: -webkit-scrollbar": { display: "none" },
              }}
            >
              <List>
                {routerConstants.map((menu, index) => {
                  const isMainMenuActive =
                    location && location.pathname.includes(menu.route);

                  if (
                    menu.permissions &&
                    !checkPermission(menu.permissions, "OR")
                  ) {
                    return null;
                  }
                  if (menu.label === "Knowledge Base" && !featureFlags.kb) {
                    return null;
                  }

                  if (menu.requiresHelena && !isHelenaActive) {
                    return null;
                  }
                  return (
                    <ListItem
                      key={menu.key}
                      disablePadding
                      sx={{
                        display: "block",
                        px: open ? "12px" : "16px",
                      }}
                    >
                      <Tooltip
                        title={menu.label}
                        placement="right"
                        popperStyle={{
                          "& .MuiTooltip-tooltip": {
                            backgroundColor: theme.palette.neutral["005"],
                            ...theme.typography["caption"],
                            color: theme.palette.lightBg.high,
                            ...(open ? { display: "none" } : {}),
                          },
                        }}
                      >
                        <ListItemButton
                          className={`${menu?.intercomClassName || ""}`}
                          disableRipple
                          sx={{
                            display: "flex",
                            justifyContent: open ? "initial" : "center",
                            color: "#FFF",
                            //   background: "rgba(255, 255, 255, 0.25)",
                            background: isMainMenuActive
                              ? open
                                ? "rgba(255, 255, 255, 0.25)"
                                : theme.palette.purple.main
                              : "transparent",
                            borderRadius: "8px",
                            // pl: "16px",
                            // pr: "8px",
                            // py: "8px",
                            padding: open ? "8px 16px" : "8px 0px",
                            height: "40px",
                            mb: "24px",
                            ":hover": {
                              background: isMainMenuActive
                                ? !open
                                  ? theme.palette.purple.hover
                                  : "rgba(255, 255, 255, 0.25)"
                                : "rgba(255, 255, 255, 0.25)",
                            },
                          }}
                          onClick={() =>
                            // @ts-ignore
                            toggleCollapse({ route: menu.route, menu })
                          }
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? "12px" : "0px",
                              justifyContent: "center",
                              position: "relative",
                            }}
                          >
                            <SidebarIcons
                              name={menu.key}
                              type={isMainMenuActive ? "filled" : "outline"}
                              htmlColor="#FFF"
                            />
                            {menu.isNew && !open && (
                              <NewDot
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: "-8px",
                                }}
                              />
                            )}
                          </ListItemIcon>

                          {open && (
                            <>
                              <ListItemText
                                primary={
                                  menu?.isBeta && open ? (
                                    <Stack direction={"row"}>
                                      {menu.label}
                                      <Typography
                                        variant="caption-3"
                                        sx={{
                                          fontWeight: "600",
                                          transform: "translate(2px,-2px)",
                                          background:
                                            "linear-gradient(135deg, #ED25C1 -8.98%, #DA64ED 47.33%, #A74FFF 66.54%, #874FFF 82.24%, #8590ff 90.70%)",
                                          WebkitTextFillColor: "transparent",
                                          WebkitBackgroundClip: "text",
                                        }}
                                      >
                                        beta
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    menu.label
                                  )
                                }
                                sx={{
                                  opacity: open ? 1 : 0,
                                  my: 0,
                                  py: 0,
                                  ...theme.typography["body-medium"],
                                  color: theme.palette.darkBg.high,
                                }}
                              />

                              {menu.isNew && <NewTag />}
                              {menu.children.length > 0 && open && (
                                <DownArrowIcon htmlColor="#FFF" />
                              )}
                              <Box
                                sx={{
                                  width: open ? "6px" : 0,
                                  height: open ? "6px" : 0,
                                  borderRadius: "100%",
                                  backgroundColor: menu.hasNew
                                    ? theme.palette.error.main
                                    : "transparent",
                                  opacity: open ? 1 : 0,
                                }}
                              ></Box>
                            </>
                          )}
                        </ListItemButton>
                      </Tooltip>
                      {menu.children.length > 0 && (
                        <Collapse
                          in={openedTab.includes(menu.route)}
                          sx={{ color: "#fff" }}
                        >
                          <List component="div" disablePadding>
                            {menu.children.map((childItem: any) => {
                              const isChildItemActive =
                                location &&
                                location.pathname.includes(childItem.route);

                              if (
                                childItem?.permissions &&
                                !checkPermission(childItem?.permissions, "OR")
                              ) {
                                return null;
                              }

                              return (
                                <Link
                                  key={childItem.defaultRoute}
                                  style={{ textDecoration: "none" }}
                                  to={childItem.defaultRoute}
                                >
                                  <ListItemButton
                                    disableRipple
                                    sx={{
                                      pl: "28px",
                                      borderRadius: "8px",
                                      height: "40px",
                                      mb: "8px",
                                      pr: "8px",
                                      background: isChildItemActive
                                        ? theme.palette.purple.main
                                        : "transparent",

                                      ":hover": {
                                        background: isChildItemActive
                                          ? theme.palette.purple.hover
                                          : "rgba(255, 255, 255, 0.25)",
                                      },
                                    }}
                                  >
                                    <ListItemIcon
                                      sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : "auto",
                                        justifyContent: "center",
                                      }}
                                    ></ListItemIcon>
                                    <ListItemText
                                      sx={{
                                        ...theme.typography["body-medium"],
                                        color: theme.palette.darkBg.high,
                                      }}
                                      primary={childItem.label}
                                    />
                                    {/* @ts-ignore */}
                                    {childItem?.isNew && (
                                      <Stack
                                        sx={{
                                          bgcolor: "#BAF4FC",
                                          padding: "2px 6px 3px 6px",
                                          borderRadius: "12px",
                                        }}
                                      >
                                        <Typography
                                          variant="caption-3"
                                          color={theme.palette.lightBg.high}
                                          sx={{
                                            fontWeight: "600",
                                          }}
                                        >
                                          New
                                        </Typography>
                                      </Stack>
                                    )}
                                  </ListItemButton>
                                </Link>
                              );
                            })}
                          </List>
                        </Collapse>
                      )}
                    </ListItem>
                  );
                })}
              </List>
              {/* <Divider sx={{ backgroundColor: "#6E717C" }} /> */}
              {/* disabled routes */}
              {/* <List>
                {disabledRoutes.map((menu, index) => {
                  const isMainMenuActive =
                    location && location.pathname.includes(menu.route);
                  return (
                    <ListItem
                      key={menu.key}
                      disablePadding
                      sx={{ display: "block", paddingX: "12px" }}
                    >
                      <ListItemButton
                        disableRipple
                        sx={{
                          justifyContent: open ? "initial" : "center",
                          color: "#6E717C",
                          cursor: "default",
                          //   background: "rgba(255, 255, 255, 0.25)",
                          background: "transparent",
                          borderRadius: "8px",
                          px: "16px",
                          py: "8px",
                          height: "40px",
                          mb: "24px",
                          // ":hover": {
                          //   background: isMainMenuActive
                          //     ? !open
                          //       ? theme.palette.purple.hover
                          //       : "rgba(255, 255, 255, 0.25)"
                          //     : "rgba(255, 255, 255, 0.25)",
                          // },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : "auto",
                            justifyContent: "center",
                          }}
                        >
                          <SidebarIcons
                            name={menu.key}
                            type={isMainMenuActive ? "filled" : "outline"}
                            htmlColor="#6E717C"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={menu.label}
                          sx={{
                            opacity: open ? 1 : 0,
                            my: 0,
                            py: 0,
                            ...theme.typography["body-medium"],
                            color: "#6E717C",
                          }}
                        />
                        {menu.children.length > 0 && open && (
                          <DownArrowIcon htmlColor="#FFF" />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List> */}
            </Box>

            <Box
              sx={{
                cursor: "pointer",
                flexShrink: 0,
                height: "fit-content",
                bgcolor: "#0D0F23",

                border: 0,
                display: "flex",
                gap: "4px",
                flexDirection: "column",
                justifyContent: "center",
                alignContent: "center",
              }}
            >
              <OnboardingProgressButton
                open={open}
                // progressPercent={progressPercentage}
              />
              <WhatsNewButton open={open} />
              {/* <OfferGift open={open} /> */}
              {checkPermission(Object.values(PERMISSIONS.CC), "OR") && (
                <Tooltip
                  title={"Control Center"}
                  placement="right"
                  popperStyle={{
                    "& .MuiTooltip-tooltip": {
                      visibility: open ? "hidden" : "visible",
                      backgroundColor: theme.palette.neutral["005"],
                      ...theme.typography["caption"],
                      color: theme.palette.lightBg.high,
                    },
                  }}
                >
                  <Box>
                    <BottomMenuItem
                      open={open}
                      isActive={
                        location &&
                        location.pathname.includes("/app/control-center") &&
                        !location.pathname.includes(
                          "/app/control-center/agent-config",
                        )
                      }
                      icon={<ControlcenterIcon />}
                      title="Control Center"
                      onClick={() => {
                        navigate("/app/control-center");
                        setOpenedTab((prev) =>
                          prev.includes("/app/control-center")
                            ? ""
                            : "/app/control-center",
                        );
                      }}
                      className="intercomV2-sideBar-controlCenterBtn"
                    />
                  </Box>
                </Tooltip>
              )}
              <Tooltip
                title={"Download Apps"}
                placement="right"
                popperStyle={{
                  "& .MuiTooltip-tooltip": {
                    visibility: open ? "hidden" : "visible",
                    backgroundColor: theme.palette.neutral["005"],
                    ...theme.typography["caption"],
                    color: theme.palette.lightBg.high,
                  },
                }}
              >
                <Box>
                  <AgentDownloadButton open={open} />
                </Box>
              </Tooltip>
            </Box>
          </Drawer>
          <Box
            component="main"
            sx={{
              position: "absolute",
              left: "80px",
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "hidden",
              width: "calc(100vw - 80px)",
              height: "100vh",
              //   width: open
              //     ? `calc(100vw - ${drawerWidth})`
              //     : `calc(100vw - 80px)`,
            }}
          >
            <DrawerHeader />
            <React.Suspense
              fallback={
                <Stack
                  sx={{
                    width: "100%",
                    height: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularLoader />
                </Stack>
              }
            >
              <Outlet />
            </React.Suspense>
          </Box>
          {havePermissionForChat && (
            <>
              <PinnedChat />
              {/* <ChatNotification /> */}
            </>
          )}
          {/* <FloatingActionButton /> */}
        </Box>
      </ReturnContext.Provider>
    </>
  );
};

export const BottomMenuItem = ({
  open,
  onClick,
  isActive,
  icon,
  title,
  className,
  styleSx,
}: {
  isActive: boolean;
  open: boolean;
  onClick?: () => void;
  title: string;
  icon: React.ReactNode;
  className?: string;
  styleSx?: SxProps<Theme>;
}) => {
  const theme = useTheme();
  return (
    <Box
      className={`${className || ""}`}
      onClick={onClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: open ? "start" : "center",
        alignItems: "center",
        gap: "20px",
        padding: open ? "8px 16px" : "8px 0px",
        mx: open ? "12px" : "16px",
        borderRadius: "8px",
        color: "#FFF !important",
        background: isActive
          ? open
            ? "rgba(255, 255, 255, 0.25)"
            : theme.palette.purple.main
          : "transparent",
        ":hover": {
          backgroundColor:
            !open && isActive
              ? theme.palette.purple.hover
              : "rgba(255, 255, 255, 0.25)",
          borderRadius: "8px",
          color: "#FFF",
        },
        ...styleSx,
      }}
    >
      {icon}
      {/* <Lottie
                  style={{ position: "absolute" }}
                  animationData={confetti}
                  loop={true}
                /> */}
      {open && (
        <Stack
          width={"100%"}
          direction={"row"}
          justifyContent={"space-between"}
        >
          <Typography variant="body-medium" color="#FFF">
            {title}
          </Typography>
          {/* <Stack
            sx={{
              bgcolor: "#BAF4FC",
              padding: "2px 6px 3px 6px",
              borderRadius: "12px",
            }}
          >
            <Typography
              variant="caption-3"
              color={theme.palette.lightBg.high}
              sx={{
                fontWeight: "600",
              }}
            >
              New
            </Typography>
          </Stack> */}
        </Stack>
      )}
    </Box>
  );
};

export const AgentDownloadButton = ({ open }: { open: boolean }) => {
  const theme = useTheme();
  const location = useLocation();
  const [isDownloadDrawerOpen, setIsDownloadDrawerOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("o") === "download") {
      setIsDownloadDrawerOpen(true);
      params.delete("o");
      window.history.replaceState(
        {},
        "",
        `${location.pathname}${params.toString() ? `?${params}` : ""}`,
      );
    }
  }, [location.search, location.pathname]);
  return (
    <>
      <Box
        className="intercom-downloadagent-btn intercomV2-sideBar-downloadAgenBtn"
        onClick={() => {
          setIsDownloadDrawerOpen(true);
        }}
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "row",
          justifyContent: open ? "start" : "center",
          alignItems: "center",
          gap: "20px",
          padding: open ? "16px 0px 16px 24px" : "16px 0px",
          ":hover": {
            backgroundColor: "rgba(255, 255, 255, 0.25)", //theme.palette.purple.main,
            color: "#FFF",
          },
        }}
      >
        <DownloadIcon htmlColor="#FFF" />
        {/* <Lottie
                  style={{ position: "absolute" }}
                  animationData={confetti}
                  loop={true}
                /> */}
        {open && (
          <Typography variant="body-medium" color="#FFF">
            Download Apps
          </Typography>
        )}
      </Box>
      {isDownloadDrawerOpen && (
        <DownloadDrawer
          open={isDownloadDrawerOpen}
          onClose={() => setIsDownloadDrawerOpen(false)}
        />
      )}
    </>
  );
};

{
  /* For animated gift box uncomment this */
}

// const OfferGift = ({ open }: { open: boolean }) => {
//   const navigate = useNavigate();
//   const {
//     data: subscriptionDetails,
//     refetch: refetchSubscriptionDetails,
//     isLoading: isSubsLoading,
//   } = useSubscriptionDetails();

//   const [showChristmasOfferBanner, setShowChristmasOfferBanner] =
//     useState(false);

//   const isSubscriptionPageActive =
//     location && location.pathname.includes("app/control-center/subscription");

//   React.useEffect(() => {
//     checkBannerVisibility();
//   }, []);

//   const checkBannerVisibility = () => {
//     const banner = localStorage.getItem("christmasOfferSideBanner");

//     if (banner === "false") {
//       return setShowChristmasOfferBanner(false);
//     }
//     setShowChristmasOfferBanner(true);
//   };

//   const handleOnChristmasBannerClose = () => {
//     localStorage.setItem("christmasOfferSideBanner", "false");
//     setShowChristmasOfferBanner(false);
//   };
//   return !isSubscriptionPageActive &&
//     showChristmasOfferBanner &&
//     !subscriptionDetails?.is_custom_plan &&
//     subscriptionDetails?.recurring_interval === "month" ? (
//     <Box
//       sx={{
//         width: "100%",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         p: "14px",
//       }}
//     >
//       <Box
//         sx={{
//           flexDirection: "column",
//           width: "100%",
//           aspectRatio: open ? "unset !important" : "1/1",
//           maxHeight: "200px",
//           background: "linear-gradient(180deg, #B989F7 0%, #8853FA 100%)",
//           borderRadius: "12px",
//           border: "0.42px solid",
//           borderImageSource:
//             "linear-gradient(134.01deg, rgba(255, 255, 255, 0) 78.35%, #E0E0E0 100%)",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           px: open ? "32px" : "0px",
//           position: "relative",
//           cursor: "pointer",
//         }}
//         onClick={() => {
//           navigate("/app/control-center/subscription");
//         }}
//       >
//         <Box
//           sx={{
//             width: "32px",
//             aspectRatio: "1/1",
//             position: "relative",
//           }}
//         >
//           <Lottie
//             animationData={Giftlottie}
//             loop={true}
//             style={{
//               width: open ? "52px" : "32px",
//               aspectRatio: "1/1",
//               transform: open ? "translateY( -50% )" : "translateY(0%)",
//               transition: "all 0.2s ease",
//               position: "absolute",
//               left: open ? "-8px" : "0px",
//             }}
//           />
//         </Box>

//         {open && (
//           <Box
//             sx={{
//               position: "absolute",
//               top: "-8px",
//               right: "-8px",
//               cursor: "pointer",
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               width: "24px",
//               height: "24px",
//               aspectRatio: "1/1",
//               bgcolor: "white",
//               borderRadius: "50%",
//               transition: open ? "all 1.2s ease" : "all 0.2s ease",
//               opacity: open ? 1 : 0,
//             }}
//             onClick={handleOnChristmasBannerClose}
//           >
//             <CloseIcon
//               sx={{
//                 height: "16px",
//                 width: "16px",
//               }}
//             />
//           </Box>
//         )}

//         <Box
//           sx={{
//             height: open ? "fit-content" : "0px",
//             transition: open ? "all 1.2s ease" : "all 0.2s ease",
//             overflow: "hidden",
//             opacity: open ? 1 : 0,
//             // position: "absolute",
//             transform: "translateY( -20% )",
//           }}
//         >
//           <Stack width={"100%"} alignItems={"center"}>
//             <Stack gap={"8px"}>
//               <Typography
//                 variant="h4"
//                 sx={{
//                   color: "#FFFFFF",
//                   textTransform: "uppercase",
//                 }}
//               >
//                 25% off
//               </Typography>
//               <Box
//                 sx={{
//                   width: "100%",
//                   height: "1.7px",
//                   background:
//                     "radial-gradient(50% 50% at 50% 50%, #CFDEFB 0%, rgba(209, 224, 245, 0) 100%)",
//                 }}
//               />
//             </Stack>
//             <Stack direction={"row"} gap={"8px"} alignItems={"center"}>
//               <InfoCloudIcon
//                 htmlColor="#FFFFFF"
//                 sx={{
//                   width: "20px",
//                   height: "20px",
//                 }}
//               />
//               <Typography
//                 variant="caption"
//                 sx={{
//                   color: "#FFFFFF",
//                 }}
//               >
//                 Both Annual and Monthly Plans
//               </Typography>
//             </Stack>
//             <Stack
//               width={"100%"}
//               sx={{
//                 mt: "8px",
//                 p: "4px",
//                 borderRadius: "4px",
//                 border: ".3px solid #FFFFFF",
//                 bgcolor: "#FFFFFF1F",
//                 alignItems: "center",
//               }}
//             >
//               <Typography
//                 variant="caption-3"
//                 sx={{
//                   color: "#FFFFFF",
//                 }}
//               >
//                 Subscribe to claim!
//               </Typography>
//             </Stack>
//           </Stack>
//         </Box>
//       </Box>
//     </Box>
//   ) : (
//     <></>
//   );
// };

export const SidebarIcons = ({
  name,
  type,
  htmlColor,
}: {
  name: string;
  type: "filled" | "outline";
  htmlColor?: string;
}) => {
  switch (name) {
    case "HOME-ROUTE-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <HomeFilledIcon />
      ) : (
        <HomeIcon htmlColor={htmlColor} />
      );
    case "SERVICE-DESK-ROUTE-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <SDFilledIcon />
      ) : (
        <SDIcon htmlColor={htmlColor} />
      );
    case "CUSTOMERS-ROUTE-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <CustomersFilledIcon />
      ) : (
        <CustomersIcon htmlColor={htmlColor} />
      );
    case "CONTROL-CENTER-ROUTE-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <ControlcenterFilledIcon />
      ) : (
        <ControlcenterIcon htmlColor={htmlColor} />
      );
    case "AI-AGENTS-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <HelenaAgentFilledIcon />
      ) : (
        <HelenaAgentIcon fill={htmlColor} />
      );
    case "DISPATCH-PORTAL-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <CalenderTimerFilledIcon />
      ) : (
        <CalenderTimerIcon htmlColor={htmlColor} />
      );

    // disabled

    case "REMOTE-CONTROL-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <RMMFilledIcon />
      ) : (
        <RMMIcon htmlColor={htmlColor} />
      );
    case "DOCUMENTATION-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <DocumentationFilledIcon />
      ) : (
        <DocumentationIcon htmlColor={htmlColor} />
      );
    case "ASSET-MANAGEMENT-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <DevicemanagementFilledIcon />
      ) : (
        <DevicemanagementIcon htmlColor={htmlColor} />
      );
    case "BILLING-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <BillingInvoiceFilledIcon />
      ) : (
        <BillingInvoiceIcon htmlColor={htmlColor} />
      );
    case "REPORTS-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <ReportsFilledIcon />
      ) : (
        <ReportsIcon htmlColor={htmlColor} />
      );
    case "BOOK-ROUTER-CONFIG-KEY":
      return type === "filled" ? (
        <BookFilledIcon />
      ) : (
        <BookIcon htmlColor={htmlColor} />
      );

    default:
      return type === "filled" ? (
        <HomeFilledIcon htmlColor={htmlColor} />
      ) : (
        <HomeIcon htmlColor={htmlColor} />
      );
  }
};
